import type { Placement } from '@floating-ui/react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import { atom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import Sizes from '../utils/responsive/Sizes';
import isSize from '../utils/responsive/isSize';
import styles from './Tooltip.module.scss';

const DETECT_OVERFLOW_OPTIONS: Partial<Parameters<typeof shift>[0]> = {
  padding: 0,
  boundary: [],
  altBoundary: false,
  mainAxis: true,
  crossAxis: false,
  limiter: limitShift(),
};

type TriggerType = 'click' | 'hover' | 'clickOrHover' | 'none';

interface PortalContainerProps {
  children: React.ReactNode;
  noPortal: boolean | undefined;
}

const PortalContainer = ({ children, noPortal }: PortalContainerProps) => {
  if (noPortal) return <>{children}</>;

  return createPortal(children, document.body);
};

const MIN_RATIO = 1 / 2;
const MAX_RATIO = 4 / 1;
const MAX_WIDTH = 120;

const checkAspectRatio =
  process.env.NODE_ENV === 'production'
    ? () => {}
    : (innerElement: HTMLDivElement) => {
        const clearTooltipError = () => {
          document.body.querySelector('.tooltip-error')?.remove();
        };

        const showTooltipError = (text: string) => {
          const error = document.createElement('div');
          error.className = 'tooltip-error';
          error.style.position = 'fixed';
          error.style.zIndex = '9999';
          error.style.top = '0';
          error.style.left = '0';
          error.style.width = '100%';
          error.style.backgroundColor = 'darkred';
          error.style.color = 'white';
          error.style.padding = '10px';
          error.style.textAlign = 'center';
          error.style.whiteSpace = 'pre-wrap';
          error.textContent = text;
          error.onclick = () => error.remove();
          document.body.appendChild(error);
        };

        const rect = innerElement.getBoundingClientRect();
        const text = innerElement.textContent;
        clearTooltipError();

        if (rect.width > MAX_WIDTH && text) {
          const ratio = rect.width / rect.height;

          if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
            showTooltipError(
              `Check tooltip aspect ratio for tooltip: "${text.replace(
                `\n`,
                ' ',
              )}".\nTooltip is ${rect.width}px wide and ${
                rect.height
              }px high, ratio is ${ratio.toFixed(
                2,
              )}, but should be between ${MIN_RATIO} and ${MAX_RATIO}`,
            );
          }
        }
      };

const OPEN_DELAY_DURATION = 1000;
const CLOSE_DELAY_DURATION = 500;

const calculateMaxWidth = (text: string) => {
  const words = text.trim().split(/\s+/).length;
  if (words >= 5 && words <= 20) {
    return 200;
  }
  if (words > 20) {
    return 300;
  }

  return undefined;
};

const stateAtom = atom<
  | {
      id: string;
      state: 'open';
    }
  | {
      id: string;
      state: 'hoverOut';
      cancel: () => void;
    }
  | {
      id?: string;
      nextId: string;
      state: 'hoverIn';
      cancel: () => void;
    }
  | { state: 'idle' }
>({ state: 'idle' });

type TooltipDistinctProps =
  | {
      variant?: 'minimal';
      content?: string;
      additionalText?: React.ReactNode;
      danger?: boolean;
    }
  | {
      variant: 'large';
      content?: React.ReactNode;
    };

type TooltipProps =
  | {
      asChild?: boolean;
      /**  @deprecated use asChild instead */
      display?: 'block' | 'inline-block' | 'inline';
      offset?: number;
      children: React.ReactNode;
      trigger?: TriggerType;
      enabledForSmallSize?: boolean;
      forceOpen?: boolean;
      placement?: Placement;
      noPortal?: boolean;
    } & TooltipDistinctProps;

const Tooltip = ({
  trigger = 'hover',
  enabledForSmallSize,
  forceOpen,
  placement = 'top',
  children,
  offset: offsetAmount = 4,
  ...props
}: TooltipProps) => {
  const id = useId();
  const arrowRef = useRef<SVGSVGElement | null>(null);

  const [innerElement, setInnerElement] = useState<HTMLDivElement | null>(null);

  const isTooltipOpenAtom = useMemo(
    () =>
      atom((get) => {
        const value = get(stateAtom);
        return value.state !== 'idle' && value.id === id;
      }),
    [id],
  );

  const isTooltipOpen = useAtomValue(isTooltipOpenAtom) || forceOpen;

  const arrowSize = props.variant === 'large' ? 10 : 4;
  const middleware = useMemo(
    () => [
      offset(offsetAmount + arrowSize),
      shift(DETECT_OVERFLOW_OPTIONS),
      flip({
        fallbackAxisSideDirection: 'end',
        ...DETECT_OVERFLOW_OPTIONS,
      }),
      arrow({ element: arrowRef, padding: 8 }),
    ],
    [offsetAmount, arrowSize],
  );

  const {
    refs,
    floatingStyles,
    middlewareData: { arrow: arrowData },
    placement: actualPlacement,
  } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  const isMobile = isSize(Sizes.SM);

  let actualTrigger = trigger;
  if (isMobile && trigger === 'hover' && enabledForSmallSize) {
    actualTrigger = 'click';
  }

  const isHoverActionAllowed =
    actualTrigger === 'hover' || actualTrigger === 'clickOrHover';

  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!innerElement || props.variant !== 'large') {
      return undefined;
    }

    const mutationObserver = new MutationObserver(() => {
      setMaxWidth(calculateMaxWidth(innerElement.textContent ?? ''));
    });

    setMaxWidth(calculateMaxWidth(innerElement.textContent ?? ''));
    mutationObserver.observe(innerElement, { subtree: true, childList: true });

    return () => {
      mutationObserver.disconnect();
    };
  }, [innerElement, props.variant]);

  useEffect(() => {
    if (innerElement) {
      checkAspectRatio(innerElement);
    }
  }, [innerElement]);

  const setIsOpen = useAtomCallback(
    useCallback(
      (get, set, isOpen: boolean) => {
        const currentState = get(stateAtom);
        if (
          currentState.state === 'hoverIn' ||
          currentState.state === 'hoverOut'
        ) {
          currentState.cancel();
        }

        if (isOpen) {
          set(stateAtom, { state: 'open', id });
        } else {
          set(stateAtom, { state: 'idle' });
        }
      },
      [id],
    ),
  );

  const onMouseEnter = useAtomCallback(
    useCallback(
      (get, set) => {
        if (!isHoverActionAllowed) return;

        const currentState = get(stateAtom);

        if (currentState.state === 'hoverOut') {
          currentState.cancel();
          set(stateAtom, { state: 'open', id });
          return;
        }

        const currentId =
          currentState.state === 'idle' ? undefined : currentState.id;

        const timeout = setTimeout(() => {
          set(stateAtom, { state: 'open', id });
        }, OPEN_DELAY_DURATION);

        set(stateAtom, {
          state: 'hoverIn',
          id: currentId,
          nextId: id,
          cancel: () => {
            clearTimeout(timeout);
          },
        });
      },
      [id, isHoverActionAllowed],
    ),
  );

  const onMouseLeave = useAtomCallback(
    useCallback(
      (get, set) => {
        if (!isHoverActionAllowed) return;

        const currentState = get(stateAtom);

        if (currentState.state === 'hoverIn') {
          currentState.cancel();
          if (currentState.id == null) {
            set(stateAtom, { state: 'idle' });
            return;
          }
        }

        const timeout = setTimeout(() => {
          set(stateAtom, { state: 'idle' });
        }, CLOSE_DELAY_DURATION);

        set(stateAtom, {
          state: 'hoverOut',
          id,
          cancel: () => {
            clearTimeout(timeout);
          },
        });
      },
      [id, isHoverActionAllowed],
    ),
  );

  if (!props.content) {
    return <>{children}</>;
  }

  const triggerContainerClassName = classNames(
    styles.triggerContainer,
    props.asChild
      ? {}
      : {
          [styles.inline]: props.display === 'inline',
          [styles.block]: props.display === 'block',
        },
  );

  const isDanger =
    (props.variant == null || props.variant === 'minimal') && props.danger;

  const tooltipProps = {
    onMouseEnter,
    onMouseLeave,
    ref: refs.setReference,
    'aria-describedby': id,
  };

  return (
    <>
      {actualTrigger === 'hover' || actualTrigger === 'none' ? (
        <>
          {props.asChild ? (
            <Slot {...tooltipProps}>{children}</Slot>
          ) : (
            <div {...tooltipProps} className={triggerContainerClassName}>
              {children}
            </div>
          )}
        </>
      ) : (
        <ClickOutsideHandler
          {...tooltipProps}
          onClickOutside={() => {
            if (isTooltipOpen) {
              setIsOpen(false);
            }
          }}
          onClick={() => {
            setIsOpen(!isTooltipOpen);
          }}
          asChild={props.asChild}
          className={triggerContainerClassName}
        >
          {children}
        </ClickOutsideHandler>
      )}
      <PortalContainer noPortal={props.noPortal}>
        {isTooltipOpen && (
          <div
            role="tooltip"
            id={id}
            ref={refs.setFloating}
            style={floatingStyles}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={classNames(
              styles.tooltip,
              styles[props.variant ?? 'minimal'],
              {
                [styles.danger]: isDanger,
              },
            )}
          >
            {(props.variant == null || props.variant === 'minimal') && (
              <>
                <div className={styles.inner} ref={setInnerElement}>
                  {props.content}
                  {!!props.additionalText && (
                    <div className={styles.additionalText}>
                      {props.additionalText}
                    </div>
                  )}
                </div>
                <svg
                  className={styles.arrow}
                  data-placement={actualPlacement}
                  style={
                    arrowData
                      ? { left: arrowData.x, top: arrowData.y }
                      : undefined
                  }
                  ref={arrowRef}
                  visibility="visible"
                  viewBox="0 0 10 4"
                >
                  <path d="M4.37531 0.499756L0 4L10 4L5.6247 0.499756C5.25948 0.207581 4.74052 0.207581 4.37531 0.499756Z" />
                </svg>
              </>
            )}
            {props.variant === 'large' && (
              <>
                <div
                  style={{ maxWidth }}
                  className={styles.inner}
                  ref={setInnerElement}
                >
                  {props.content}
                </div>

                <svg
                  data-placement={placement}
                  style={
                    arrowData
                      ? { left: arrowData.x, top: arrowData.y }
                      : undefined
                  }
                  className={styles.arrow}
                  ref={arrowRef}
                  viewBox="0 0 18 10"
                >
                  <path
                    className={styles.arrowBg}
                    d="M9.95385 1.71898L17 9L1 9L8.53945 1.69616C8.93627 1.31174 9.56964 1.32196 9.95385 1.71898Z"
                  />
                  <path
                    className={classNames(styles.arrowBg, styles.arrowStroke)}
                    d="M17 9L9.95385 1.71898C9.56964 1.32196 8.93627 1.31174 8.53945 1.69616L1 9"
                  />
                  <rect
                    className={styles.arrowBg}
                    x="18"
                    y="9"
                    width="1"
                    height="18"
                    transform="rotate(90 18 9)"
                  />
                </svg>
              </>
            )}
          </div>
        )}
      </PortalContainer>
    </>
  );
};

export default Tooltip;
